import React, { useState, useEffect } from "react";

import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import moment from "moment";

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";

import { UseIsAdmin, UseUserId } from "../../hooks/auth";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import ClaimedIcon from "../../img/Claimed.svg";

import { T, CLAIM_FONT, SOURCE_META } from "./claimTokens";

/* -------------------------------------------------------------------------- */

const Stars = ({ rating }) => {
  const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  if (!r) return null;
  return (
    <span style={{ display: "inline-flex", gap: "2px", color: T.gold, flexShrink: 0 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          // eslint-disable-next-line react/no-array-index-key
          key={`star-${i}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill={i < r ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <path d="M12 2l2.9 6.26 6.1.55-4.6 4.04 1.36 6.15L12 16.9 6.24 19l1.36-6.15L3 8.81l6.1-.55L12 2z" />
        </svg>
      ))}
    </span>
  );
};

const ConfirmDialog = ({ open, onClose, tone, title, body, cancelLabel, confirmLabel, onConfirm, confirmDisabled }) => (
  <Dialog
    open={open}
    onClose={onClose}
    sx={{
      "& .MuiPaper-root": {
        fontFamily: CLAIM_FONT,
        padding: { mini: "20px", md: "28px" },
        borderRadius: "20px",
        maxWidth: "420px",
        width: "100%",
        background: "linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.7) 100%)",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        border: "1px solid rgba(255,255,255,0.6)",
        boxShadow: "0 24px 48px -12px rgba(16,24,40,0.22), inset 0 1px 0 rgba(255,255,255,0.7)",
      },
    }}
  >
    <SoftBox display="flex" justifyContent="flex-end">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          display: "flex",
          border: "none",
          background: "rgba(15,23,42,0.05)",
          borderRadius: "50%",
          padding: "5px",
          cursor: "pointer",
          color: T.muted,
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: "1.15rem" }} />
      </button>
    </SoftBox>

    <SoftBox display="flex" flexDirection="column" alignItems="center" textAlign="center" px={1}>
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: tone === "bad" ? "#FDECEA" : T.goodSoft,
          color: tone === "bad" ? "#C2453A" : T.good,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          {tone === "bad" ? <path d="M12 9v4M12 17h.01M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" /> : <path d="M20 6 9 17l-5-5" />}
        </svg>
      </span>
      <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontWeight: 700, color: T.ink, fontSize: "18px", mt: "14px", letterSpacing: "-0.01em" }}>
        {title}
      </SoftTypography>
      <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontWeight: 400, color: T.muted, fontSize: "13.5px", lineHeight: 1.5, mt: "6px", maxWidth: "34ch" }}>
        {body}
      </SoftTypography>
    </SoftBox>

    <SoftBox display="flex" gap={1.5} mt="22px">
      <SoftButton
        fullWidth
        color="white"
        variant="outlined"
        onClick={onClose}
        sx={{
          fontFamily: CLAIM_FONT,
          textTransform: "none",
          borderRadius: "10px",
          fontWeight: 600,
          color: `${T.ink2} !important`,
          borderColor: "#D9DEE7 !important",
          backgroundColor: "rgba(255,255,255,0.55) !important",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.85) !important" },
        }}
      >
        {cancelLabel}
      </SoftButton>
      <SoftButton
        fullWidth
        variant="contained"
        color={tone === "bad" ? "error" : "primary"}
        onClick={onConfirm}
        disabled={confirmDisabled}
        sx={{ fontFamily: CLAIM_FONT, textTransform: "none", borderRadius: "10px", fontWeight: 700 }}
      >
        {confirmLabel}
      </SoftButton>
    </SoftBox>
  </Dialog>
);

/* -------------------------------------------------------------------------- */

function ClaimReviewCard({ handleClaim, item, assignPopUp, handleUnassign, handleSetClaimable }) {
  const isClaimable = item.claimable !== false;
  const [isClaimed, setIsClaimed] = useState(
    Array.isArray(item.kpi) && item.kpi.some((k) => k.ownerId),
  );
  const { type } = item;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const isAdmin = UseIsAdmin();
  const currentUserId = UseUserId();

  const isOwnClaim =
    Array.isArray(item.kpi) &&
    item.kpi.some((k) => String(k.ownerId) === String(currentUserId));

  useEffect(() => {
    setIsClaimed(Array.isArray(item.kpi) && item.kpi.some((k) => k.ownerId));
  }, [item.kpi]);

  const handleConfirmClaim = () => {
    if (isClaimed) return;
    setConfirmOpen(false);
    setIsClaimed(true);
    handleClaim(item._id, undefined, type);
  };
  const handleConfirmUnassign = () => {
    setUnassignOpen(false);
    if (typeof handleUnassign === "function") handleUnassign(item._id);
  };

  const canManageClaimable = isAdmin && typeof handleSetClaimable === "function";
  const source = SOURCE_META[type] || SOURCE_META.review;

  // Owner chips — one per distinct ownerId. Deduped so a former employee whose
  // profile no longer resolves still shows (labelled), rather than vanishing.
  const owners = (() => {
    if (!Array.isArray(item.kpi)) return [];
    const seen = new Set();
    return item.kpi.filter((k) => {
      const key = String(k.ownerId || "");
      if (!k.ownerId || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  const ghostBtnSx = {
    fontFamily: CLAIM_FONT,
    fontSize: "12.5px",
    fontWeight: 600,
    textTransform: "none",
    minWidth: "unset",
    padding: "4px 8px",
    borderRadius: "8px",
    boxShadow: "none !important",
    border: "none !important",
  };

  const renderActions = () => {
    if (isClaimed) {
      return (
        <SoftBox
          sx={{
            display: "flex",
            flexDirection: { mini: "column", sm: "row" },
            alignItems: { mini: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <SoftBox sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", minWidth: 0 }}>
            <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontSize: "12.5px", fontWeight: 600, color: T.faint, flexShrink: 0 }}>
              Assigned to
            </SoftTypography>
            {owners.length
              ? owners.map((k, idx) => (
                  <span
                    key={String(k.ownerId) || idx}
                    style={{
                      background: T.neutralChip,
                      borderRadius: "999px",
                      padding: "3px 10px",
                      fontFamily: CLAIM_FONT,
                      fontWeight: 600,
                      fontSize: "12px",
                      lineHeight: "16px",
                      color: T.ink2,
                    }}
                  >
                    {k.user
                      ? `${k.user.firstName}${k.user.lastName ? ` ${k.user.lastName}` : ""}`
                      : "Former employee"}
                  </span>
                ))
              : null}
          </SoftBox>
          <SoftBox sx={{ display: "flex", gap: "4px", flexShrink: 0, justifyContent: { mini: "flex-end", sm: "flex-start" } }}>
            {isAdmin ? (
              <>
                <SoftButton variant="text" color="primary" onClick={() => assignPopUp(item._id)} sx={ghostBtnSx}>
                  Reassign
                </SoftButton>
                <SoftButton variant="text" color="error" onClick={() => setUnassignOpen(true)} sx={ghostBtnSx}>
                  Unassign
                </SoftButton>
              </>
            ) : (
              isOwnClaim && (
                <SoftButton variant="text" color="error" onClick={() => setUnassignOpen(true)} sx={ghostBtnSx}>
                  Unclaim
                </SoftButton>
              )
            )}
          </SoftBox>
        </SoftBox>
      );
    }

    if (!isClaimable) {
      // The "Not claimable" pill in the meta row already says what's going
      // on — the foot only exists here to give an admin the one-click
      // restore.
      if (!canManageClaimable) return null;
      return (
        <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
          <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontSize: "12.5px", fontWeight: 600, color: T.faint }}>
            Not in the claimable pool
          </SoftTypography>
          <SoftButton variant="text" color="primary" onClick={() => handleSetClaimable(item._id, true)} sx={ghostBtnSx}>
            Restore
          </SoftButton>
        </SoftBox>
      );
    }

    return (
      <SoftBox sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <SoftButton
          fullWidth
          variant="contained"
          color="primary"
          onClick={isAdmin ? () => assignPopUp(item._id) : () => setConfirmOpen(true)}
          sx={{
            fontFamily: CLAIM_FONT,
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 700,
            fontSize: "14px",
            py: "8px",
          }}
        >
          {isAdmin ? "Assign now" : "Claim now"}
          <ArrowForwardRoundedIcon sx={{ fontSize: "1.05rem !important", ml: "6px" }} />
        </SoftButton>
        {canManageClaimable && (
          <Tooltip title="Remove this review from the claimable pool (e.g. it credits a former employee)">
            <SoftButton variant="text" color="secondary" onClick={() => handleSetClaimable(item._id, false)} sx={{ ...ghostBtnSx, color: `${T.faint} !important`, flexShrink: 0 }}>
              Not claimable
            </SoftButton>
          </Tooltip>
        )}
      </SoftBox>
    );
  };

  const actions = renderActions();

  return (
    <Card
      sx={{
        position: "relative",
        fontFamily: CLAIM_FONT,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: "16px",
        border: `1px solid ${T.hairline}`,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 6px 16px -8px rgba(16,24,40,0.10)",
        overflow: "hidden",
        transition: "box-shadow 180ms ease, transform 180ms ease",
        "&:hover": {
          boxShadow: "0 1px 2px rgba(16,24,40,0.05), 0 14px 32px -12px rgba(16,24,40,0.18)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Claimed watermark — tucked into the top-right corner, behind every
          other element, low-opacity enough to never fight the meta chip or
          the name/rating row for attention. Clipped by the card's own
          rounded corner via the Card's overflow: hidden. */}
      {isClaimed && (
        <SoftBox
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: "-14px",
            right: "-14px",
            width: "108px",
            height: "108px",
            backgroundImage: `url(${ClaimedIcon})`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            opacity: 0.1,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        tone="good"
        title="Claim this review"
        body={`Claim the review from ${item.name || "this customer"}? The points land on your scorecard.`}
        cancelLabel="Cancel"
        confirmLabel="Yes, claim it"
        onConfirm={handleConfirmClaim}
        confirmDisabled={isClaimed}
      />
      <ConfirmDialog
        open={unassignOpen}
        onClose={() => setUnassignOpen(false)}
        tone="bad"
        title={isAdmin ? "Unassign this review" : "Unclaim this review"}
        body={
          isAdmin
            ? "It returns to the unclaimed pool and the points are removed from the current player(s)."
            : "It returns to the unclaimed pool and its points are removed from your scorecard."
        }
        cancelLabel="Keep it"
        confirmLabel={isAdmin ? "Yes, unassign" : "Yes, unclaim"}
        onConfirm={handleConfirmUnassign}
      />

      <SoftBox
        sx={{
          position: "relative",
          zIndex: 1,
          p: { mini: "18px", sm: "22px" },
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        {/* meta */}
        <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
          <SoftBox sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: source.dot, flexShrink: 0 }} />
            <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontSize: "12px", fontWeight: 600, color: T.muted }}>
              {source.label}
            </SoftTypography>
            <span style={{ color: T.faint }}>·</span>
            <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontSize: "12px", fontWeight: 500, color: T.faint }}>
              {moment(item.createdAt).format("MMM D, YYYY")}
            </SoftTypography>
          </SoftBox>
          {!isClaimed && !isClaimable && (
            <span
              style={{
                background: "#FDECEA",
                color: "#C2453A",
                borderRadius: "999px",
                padding: "3px 9px",
                fontFamily: CLAIM_FONT,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.01em",
                flexShrink: 0,
              }}
            >
              Not claimable
            </span>
          )}
        </SoftBox>

        {/* name + rating */}
        <SoftBox sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px", mt: "13px" }}>
          <SoftTypography
            sx={{
              fontFamily: CLAIM_FONT,
              fontWeight: 700,
              fontSize: "17px",
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
              color: T.ink,
              textTransform: "capitalize",
            }}
          >
            {item.name || "Customer"}
          </SoftTypography>
          <Stars rating={item.rating} />
        </SoftBox>

        {/* the review — the hero. A hanging quotation mark instead of a rule:
            it reads as a pull-quote and ties every card together in gold. */}
        <SoftBox
          component="blockquote"
          sx={{
            m: 0,
            mt: "14px",
            pt: "14px",
            flex: 1,
            position: "relative",
            fontFamily: CLAIM_FONT,
            fontStyle: "italic",
            fontSize: "14.5px",
            lineHeight: 1.55,
            color: T.ink2,
            display: "-webkit-box",
            WebkitLineClamp: 7,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            "&::before": {
              content: '"\\201C"',
              position: "absolute",
              left: "-2px",
              top: "-6px",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: "normal",
              fontSize: "40px",
              lineHeight: 1,
              color: T.gold,
              pointerEvents: "none",
            },
          }}
        >
          {item.content || "No review text provided."}
        </SoftBox>

        {item.service && (
          <SoftTypography
            sx={{
              fontFamily: CLAIM_FONT,
              mt: "12px",
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: T.faint,
              textTransform: "lowercase",
              "&::first-letter": { textTransform: "uppercase" },
            }}
          >
            {item.service}
          </SoftTypography>
        )}
      </SoftBox>

      {/* action zone — pinned foot, only when there is something to show */}
      {actions && (
        <SoftBox sx={{ borderTop: `1px solid ${T.hairline}`, background: T.foot, p: { mini: "13px 18px", sm: "14px 22px" } }}>
          {actions}
        </SoftBox>
      )}
    </Card>
  );
}

export default ClaimReviewCard;
